import { useState } from "react";
import { toast } from "sonner";
import { ArrowUpRight, Check, Upload } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const empty = { name: "", email: "", location: "", photo: "", story: "" };

export default function SubmitPhotoForm() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Image must be under 4MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, photo: reader.result }));
    reader.readAsDataURL(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.photo) { toast.error("Please add a photo (upload or paste a URL)"); return; }
    setSending(true);
    try {
      await api.post("/potm-submissions", form);
      setDone(true);
      toast.success("Submission received", { description: "Thanks — we review every entry." });
      setForm(empty);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit");
    } finally { setSending(false); }
  };

  if (done)
    return (
      <div data-testid="potm-submit-success" className="mt-14 border border-ink p-8 sm:p-10 flex flex-col items-start gap-4">
        <span className="h-12 w-12 rounded-full bg-canary grid place-items-center"><Check /></span>
        <h3 className="font-display text-3xl font-bold">Thank you!</h3>
        <p className="text-ink/70">Your photo and story are in. If selected, we'll reach out to feature you.</p>
      </div>
    );

  return (
    <div className="mt-14 bg-paper p-8 sm:p-10" data-testid="potm-submit-block">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <p className="font-display text-2xl font-bold">Want your photo featured?</p>
          <p className="text-ink/70 mt-1">Subscribers can submit a photo and the story behind it.</p>
        </div>
        {!open && (
          <Button onClick={() => setOpen(true)} data-testid="potm-submit-open"
            className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink h-13 px-8 py-5 label">
            Submit your photo <ArrowUpRight size={15} className="ml-2" />
          </Button>
        )}
      </div>

      {open && (
        <form onSubmit={submit} data-testid="potm-submit-form" className="mt-8 space-y-5">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="label">Your name *</Label><Input required value={form.name} onChange={set("name")} data-testid="potm-sub-name" className="mt-2 rounded-none border-ink h-12" /></div>
            <div><Label className="label">Email *</Label><Input type="email" required value={form.email} onChange={set("email")} data-testid="potm-sub-email" className="mt-2 rounded-none border-ink h-12" /></div>
          </div>
          <div><Label className="label">Where was it taken?</Label><Input value={form.location} onChange={set("location")} data-testid="potm-sub-location" className="mt-2 rounded-none border-ink h-12" placeholder="Neighborhood, city" /></div>
          <div>
            <Label className="label">Photo *</Label>
            <div className="mt-2 flex flex-col sm:flex-row gap-3">
              <label data-testid="potm-sub-upload" className="cursor-pointer inline-flex items-center gap-2 label border border-ink px-5 py-3 hover:bg-ink hover:text-white transition-colors">
                <Upload size={15} /> Upload image
                <input type="file" accept="image/*" onChange={onFile} className="hidden" />
              </label>
              <Input value={form.photo.startsWith("data:") ? "" : form.photo} onChange={set("photo")} data-testid="potm-sub-photo-url" className="rounded-none border-ink h-12 flex-1" placeholder="…or paste an image URL" />
            </div>
            {form.photo && <img src={form.photo} alt="preview" className="mt-3 h-28 w-auto object-cover border border-ink/20" />}
          </div>
          <div><Label className="label">The story behind it *</Label><Textarea required rows={5} value={form.story} onChange={set("story")} data-testid="potm-sub-story" className="mt-2 rounded-none border-ink" placeholder="What's happening in the photo, and why does it matter to you?" /></div>
          <div className="flex gap-3">
            <Button type="submit" disabled={sending} data-testid="potm-sub-submit" className="rounded-full bg-ink text-white hover:bg-canary hover:text-ink h-13 px-8 py-5 label">
              {sending ? "Submitting…" : "Submit entry"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="label">Cancel</Button>
          </div>
        </form>
      )}
    </div>
  );
}
